/* eslint-disable no-lonely-if */
/* eslint-disable no-nested-ternary */
import React, {
  Fragment,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CircularProgress, IconButton } from '@material-ui/core';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Print from '@material-ui/icons/Print';
import SettingsIcon from '@material-ui/icons/Settings';
import {
  Loader,
  ConfirmModal,
  ActionIcon,
  usePrintDispatch,
} from '@mummssoftware/common-ui';
import {
  Edit,
  Delete,
  View,
  FAJITA,
  STRING_TRUE,
  Submitted,
} from '@mummssoftware/utils/constants';
import {
  ENTITY_MANIFEST,
  GlobalContext,
  FormsGlobalContext,
  PNCounterIcon,
  SET_FAJITA_QUESTIONNAIRE_FORM,
  SET_AG_GRID_SELECTED_ROWS,
  VisitsGlobalContext,
  SET_REFRESH_VISIT,
} from '@mummssoftware/global-context';
import { Printing } from '@mummssoftware/utils';
import {
  getAcceptedHopeAttributes,
  isHopeMilestoneApproved,
  isHopeNvrOrInaFormKey,
} from '@mummssoftware/utils/hope';
import { getVisitByPersonAndVisitId } from '@mummssoftware/utils/pimReader';
import { createPortal } from 'react-dom';
import { CellDiv } from '../../components/layout/WrapperDiv';
import { Context } from '../../context';
import { FORM_NAME } from './constants';
import defaults from '../../translations/en.json';

/** ag-grid-community: row click skips selection when this is set on the native click (see RowController.onRowClick). */
const AG_GRID_STOP_ROW_CLICK_PROPAGATION = '__ag_Grid_Stop_Propagation';

function stopAgGridRowClickSelection(ev: React.SyntheticEvent) {
  ((ev.nativeEvent as unknown) as Record<string, boolean>)[
    AG_GRID_STOP_ROW_CLICK_PROPAGATION
  ] = true;
}

export const CreatedByCell = React.memo((props: any) => {
  const {
    createpersonfirstname,
    createpersoncredentials,
    createpersonlastname,
  } = props.data;
  const credential = createpersonlastname
    ? `${createpersonfirstname} ${createpersonlastname}, ${createpersoncredentials}`
    : '';
  return <CellDiv>{credential}</CellDiv>;
});

export const EditCellRenderer = React.memo((props: any) => {
  const {
    currentUser,
    agency,
    readOnly,
    deleteManifestForm,
    keycloak,
    hopePatientEvent,
  } = useContext(Context);
  const { dispatchQuestionnaire } = useContext(FormsGlobalContext);
  const { completedPatientForms } = useContext(GlobalContext);
  const { dispatchVG } = useContext(VisitsGlobalContext);

  const isCompleteMode = !!completedPatientForms.length;
  const [anchorEl, setAnchorEl] = useState(null);
  const [modalOption, setModalOption] = useState<boolean>(false);
  const [acceptedAtts, setAcceptedAtts] = useState<any[]>([]);
  /** True while HOPE visit attrs are loading (WIP mode only); menu opens immediately and shows a spinner until then. */
  const [hopeAttrsLoading, setHopeAttrsLoading] = useState(false);
  const hopeVisitFetchGenRef = useRef(0);
  const formData = props.data;
  const isUser = currentUser.personNumber === formData.createpersonnumber;
  const canEdit = currentUser.isHospiceAdmin || isUser;
  const isHopeForm = isHopeNvrOrInaFormKey(formData?.formkey);
  const hopeApproved =
    isCompleteMode &&
    isHopeForm &&
    isHopeMilestoneApproved(
      formData?.formkey,
      hopePatientEvent,
      [],
      formData?.patientvisit_id || null,
    );

  let cellOptions = [View];
  if (readOnly) {
    cellOptions = [View];
  } else if (canEdit) {
    if (isCompleteMode) {
      if (isHopeForm && hopeApproved) {
        cellOptions = [View];
      } else if (isHopeForm) {
        cellOptions = [View, Edit];
      } else {
        cellOptions = [View];
      }
    } else if (acceptedAtts.length > 0) {
      cellOptions = [View, Edit];
    } else {
      cellOptions = [View, Delete, Edit];
    }
  } else {
    cellOptions = [View];
  }
  const readOnlyViewPossible =
    formData && (formData as any).formisreadonlyfullallowed === STRING_TRUE;

  const closeMenu = () => {
    hopeVisitFetchGenRef.current += 1;
    setHopeAttrsLoading(false);
    setAnchorEl(null);
  };

  const handleEdit = async (event: any) => {
    stopAgGridRowClickSelection(event);
    event.stopPropagation();
    const menuAnchor = event.currentTarget;
    const requestId = ++hopeVisitFetchGenRef.current;

    setAcceptedAtts([]);

    const submittedStatus = formData.status === Submitted;
    const visitId = formData?.patientvisit_id;
    const personNo = formData?.createpersonnumber;
    // Visit attrs fetch is only needed in WIP mode to determine HOPE visit attributes
    // for menu option gating. In Completed mode, hopeApproved is computed synchronously
    // from hopePatientEvent (pre-fetched at widget load).
    const needsHopeVisitFetch =
      !isCompleteMode &&
      isHopeForm &&
      submittedStatus &&
      keycloak &&
      agency &&
      visitId != null &&
      visitId !== '' &&
      personNo;

    setAnchorEl(menuAnchor);

    if (!needsHopeVisitFetch) {
      setHopeAttrsLoading(false);
      return;
    }

    setHopeAttrsLoading(true);
    try {
      const res = await getVisitByPersonAndVisitId(
        String(personNo),
        String(visitId),
        agency as string,
        keycloak!,
        formData.patientnumber,
      );
      if (requestId !== hopeVisitFetchGenRef.current) {
        return;
      }
      const visit = Array.isArray(res) ? res[0] : res;
      if (visit?.visitattrsumdto) {
        setAcceptedAtts(getAcceptedHopeAttributes(visit.visitattrsumdto));
      }
    } catch (err) {
      if (requestId === hopeVisitFetchGenRef.current) {
        console.error('getVisitByPersonAndVisitId (completed-forms)', err);
      }
    } finally {
      if (requestId === hopeVisitFetchGenRef.current) {
        setHopeAttrsLoading(false);
      }
    }
  };

  const handleDeleteOption = () => {
    deleteManifestForm(formData, agency as string);
    closeMenu();
    // clear selected row
    props.api.deselectAll();
    dispatchVG({ type: SET_REFRESH_VISIT, payload: null });
  };

  const handleViewOption = () => {
    formData.isInProgressFAJITA = false;
    if (readOnlyViewPossible) {
      formData.isReadOnlyViewFAJITA = true;
      formData.isPrintViewFAJITA = false;
    } else {
      formData.isReadOnlyViewFAJITA = false;
      formData.isPrintViewFAJITA = true;
    }

    // backend call form and patientmanifest
    formData.patientmanifest_id = formData.id;
    dispatchQuestionnaire({
      type: SET_FAJITA_QUESTIONNAIRE_FORM,
      payload: formData as mumms.FajitaForm,
    });
    const formName = `${FAJITA}:${formData.formkey}`;
    localStorage.setItem(FORM_NAME, formName);
    closeMenu();
    // clear selected row
    props.api.deselectAll();
  };

  const handleEditOption = () => {
    // this is to match what <FajitaForm /> for Patient is expected
    formData.isInProgressFAJITA = true;
    formData.isPrintViewFAJITA = false;
    formData.isReadOnlyViewFAJITA = false;

    // backend call form and patientmanifest
    formData.patientmanifest_id = formData.id;
    dispatchQuestionnaire({
      type: SET_FAJITA_QUESTIONNAIRE_FORM,
      payload: formData as mumms.FajitaForm,
    });
    const formName = `${FAJITA}:${formData.formkey}`;
    localStorage.setItem(FORM_NAME, formName);
    closeMenu();
    // clear selected row
    props.api.deselectAll();
  };

  const selectOption = (option: string) => {
    if (isCompleteMode) {
      switch (option) {
        case View:
          return handleViewOption();
        case Edit:
          return handleEditOption();
        case Delete:
          return setModalOption(true);
        default:
          return null;
      }
    } else {
      switch (option) {
        case Edit:
          return handleEditOption();
        case Delete:
          return setModalOption(true);
        case View:
          return handleViewOption();
        default:
          return null;
      }
    }
  };

  const onCancelClick = () => {
    closeMenu();
    setModalOption(false);
  };

  const onClickDelete = () => {
    handleDeleteOption();
    setModalOption(false);
  };

  const getMenuUI = () =>
    modalOption ? (
      <ConfirmModal
        onOKClick={onClickDelete}
        onCancelClick={onCancelClick}
        okText={defaults.yes}
        noText={defaults.no}
        modalText={defaults.confirmDelete}
      />
    ) : hopeAttrsLoading ? (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: 16,
          minWidth: 120,
        }}
      >
        <CircularProgress size={24} />
      </div>
    ) : (
      <>
        {cellOptions.map((option: string) => (
          <MenuItem
            key={option}
            onClick={() => selectOption(option)}
            id="cross-form-edit"
          >
            {option}
          </MenuItem>
        ))}
      </>
    );

  return (
    <CellDiv onClickCapture={(e) => stopAgGridRowClickSelection(e)}>
      <IconButton onClick={handleEdit} size="small" disabled={false}>
        <SettingsIcon />
      </IconButton>
      <Fragment>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          <div>{getMenuUI()}</div>
        </Menu>
      </Fragment>
    </CellDiv>
  );
});

// not used
export const PrintCellRenderer = (props: any) => {
  const formData = props.data;

  const { keycloak, agency, currentUser, patient } = useContext(Context);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const printingService = useMemo(
    () =>
      keycloak && currentUser
        ? new Printing({
            url: window.env.hosts.printingService as string,
            keycloak,
            agency,
            currentUser,
          })
        : null,
    [keycloak, agency, currentUser],
  );

  const getPrintPromise = useCallback(
    (faxInstructions: any, emailInstructions: any) =>
      printingService!.printManifest(
        [formData],
        patient!,
        undefined,
        undefined,
        faxInstructions,
        emailInstructions,
      ),
    [printingService, formData, patient],
  );

  const { print, fax, email } = usePrintDispatch(
    getPrintPromise,
    () => {
      setAnchorEl(null);
      props.api.deselectAll();
      setLoading(true);
    },
    () => setLoading(false),
  );

  const guard = (fn: () => void) => () => {
    if (!keycloak || !patient || !printingService) return;
    fn();
  };

  if (!keycloak || !patient || !printingService) {
    return <></>;
  }

  return (
    <CellDiv onClickCapture={(e) => stopAgGridRowClickSelection(e)}>
      <IconButton
        size="small"
        onClick={(e) => {
          stopAgGridRowClickSelection(e);
          setAnchorEl(e.currentTarget);
        }}
      >
        <Print />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={guard(print)}>Print</MenuItem>
        <MenuItem onClick={guard(fax)}>Fax</MenuItem>
        <MenuItem onClick={guard(email)}>Email</MenuItem>
      </Menu>
      {loading && createPortal(<Loader />, document.body)}
    </CellDiv>
  );
};

export const PrintWidgetHeader = () => {
  const { keycloak, agency, currentUser, patient } = useContext(Context);
  const globalContext = useContext(GlobalContext);
  const { agGridSelectedRows } = useContext(GlobalContext);

  const printActive = agGridSelectedRows && agGridSelectedRows.length;

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const printingService = useMemo(
    () =>
      keycloak && currentUser
        ? new Printing({
            url: window.env.hosts.printingService as string,
            keycloak,
            agency,
            currentUser,
          })
        : null,
    [keycloak, agency, currentUser],
  );

  const getPrintPromise = useCallback(
    (faxInstructions: any, emailInstructions: any) =>
      printingService!
        .printManifest(
          agGridSelectedRows as any,
          patient!,
          undefined,
          undefined,
          faxInstructions,
          emailInstructions,
        )
        .then((res: any) => {
          setTimeout(() => {
            globalContext.dispatch({
              type: SET_AG_GRID_SELECTED_ROWS,
              payload: null,
            });
          }, 500);
          return res;
        }),
    [printingService, agGridSelectedRows, patient, globalContext],
  );

  const {
    print: printAction,
    fax: faxAction,
    email: emailAction,
  } = usePrintDispatch(
    getPrintPromise,
    () => {
      setAnchorEl(null);
      setLoading(true);
    },
    () => setLoading(false),
  );

  const guardActive = (fn: () => void) => () => {
    if (printActive) fn();
  };

  if (!keycloak || !patient || !printingService) {
    return <></>;
  }

  return (
    <>
      <ActionIcon
        title={defaults.print}
        Icon={Print}
        onClick={(e: any) => printActive && setAnchorEl(e?.currentTarget)}
        disabled={!printActive}
        style={{
          margin: 0,
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={guardActive(printAction)}>Print</MenuItem>
        <MenuItem onClick={guardActive(faxAction)}>Fax</MenuItem>
        <MenuItem onClick={guardActive(emailAction)}>Email</MenuItem>
      </Menu>
      {loading && createPortal(<Loader />, document.body)}
    </>
  );
};
export const NoteCountCellRenderer = (props: any) =>
  props?.data?.id && (
    <PNCounterIcon
      patientNumber={props?.data.patientnumber}
      // eslint-disable-next-line camelcase
      entityKey={props?.data?.id.toString()}
      // eslint-disable-next-line camelcase
      entityName={props?.data?.formname}
      entityType={ENTITY_MANIFEST}
    />
  );
